import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import { Panel, ToolbarButton } from '../../../ui-kit';
import { fetchCqConfiguration, startQuote } from '../../../services/carrierQuotingService';
import { useLive } from '../rocco/live';
import { useNavigate } from 'react-router-dom';
import { sizeOf } from './status';

/** What a household usually sends, and what the agent takes from each. */
const SLOTS = [
  { key: 'dec_page', label: 'Declarations page', detail: 'The policy they have today: limits, deductibles, vehicles and what they pay' },
  { key: 'application', label: 'Quote form or application', detail: 'Typed or filled in by hand: how each vehicle is used, miles a year, tickets and accidents' },
  { key: 'license', label: 'Driving licences', detail: 'A photo is fine: name, date of birth and licence number' },
];

/**
 * Where a quote starts: drop in what the household sent.
 *
 * Nobody is asked which document is which, or to type anything in. The agent
 * works out what each file is, reads it, and puts one customer file together,
 * which is the first thing worth showing.
 */
export default function NewQuotePage() {
  const { data: setup } = useLive(fetchCqConfiguration, 120_000);
  const [files, setFiles] = React.useState<File[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const [over, setOver] = React.useState(false);
  const input = React.useRef<HTMLInputElement>(null);

  const limits = setup?.limits;
  const add = (incoming: FileList | null) => {
    if (!incoming) return;
    setProblem(null);
    const room = (limits?.max_files ?? 10) - files.length;
    const chosen = Array.from(incoming).slice(0, Math.max(0, room));
    const tooBig = chosen.find((f) => f.size > (limits?.max_mb ?? 20) * 1024 * 1024);
    if (tooBig) {
      setProblem(`${tooBig.name} is larger than ${limits?.max_mb ?? 20} MB`);
      return;
    }
    setFiles((have) => [...have, ...chosen.filter((f) => !have.some((x) => x.name === f.name && x.size === f.size))]);
  };

  const run = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const quote = await startQuote(files);
      setFiles([]);
      // Its own page, which follows the reading and everything after it.
      navigate(`/agents/carrier_quoting/quotes/${quote.id}`);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not upload those documents');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Panel
        title="Quote a household"
        subtitle="Drop in what the customer sent for their auto insurance. The agent reads it, asks the carriers and builds the proposal."
      >
        <Stack spacing={2}>
          <Box
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
            onClick={() => input.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') input.current?.click(); }}
            sx={(t) => ({
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: `${t.ds.radius.md}px`,
              border: `1.5px dashed ${over ? t.tokens.brand.primary : t.tokens.border.default}`,
              bgcolor: over ? t.tokens.brand.primarySofter : t.tokens.surface.sunken,
              transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              '&:hover': { borderColor: t.tokens.brand.primary },
              '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing, outline: 'none' },
            })}
          >
            <UploadFileRounded sx={(t) => ({ fontSize: 30, color: t.tokens.text.tertiary })} />
            <Typography variant="subtitle1" sx={(t) => ({ color: t.tokens.text.primary, mt: 0.5 })}>
              Drop the documents here, or choose files
            </Typography>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {limits ? `Up to ${limits.max_files} files, ${limits.max_mb} MB each · ${limits.accepted}`
                : 'PDF, Word, plain text, or a photo'}
            </Typography>
            <input
              ref={input}
              type="file"
              multiple
              accept=".pdf,.txt,.md,.docx,.jpg,.jpeg,.png,.webp"
              hidden
              onChange={(e) => { add(e.target.files); e.target.value = ''; }}
            />
          </Box>

          {files.length ? (
            <Stack spacing={1}>
              {files.map((file) => (
                <Stack
                  key={`${file.name}-${file.size}`}
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={(t) => ({
                    px: 1.25, py: 1,
                    borderRadius: `${t.ds.radius.sm}px`,
                    border: `1px solid ${t.tokens.border.subtle}`,
                  })}
                >
                  {file.type.startsWith('image/')
                    ? <ImageRounded sx={(t) => ({ fontSize: 18, color: t.tokens.text.tertiary })} />
                    : <DescriptionRounded sx={(t) => ({ fontSize: 18, color: t.tokens.text.tertiary })} />}
                  <Typography variant="body2" sx={(t) => ({ flex: 1, minWidth: 0, color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                    {sizeOf(file.size)}
                  </Typography>
                  <ToolbarButton
                    variant="ghost"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setFiles((have) => have.filter((f) => f !== file))}
                  >
                    Remove
                  </ToolbarButton>
                </Stack>
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <ToolbarButton variant="brand" size="medium" icon={<PlayArrowRounded />} onClick={run}
              loading={busy} loadingLabel="Uploading" disabled={!files.length}>
              {files.length ? `Read these ${files.length} document${files.length === 1 ? '' : 's'}` : 'Read the documents'}
            </ToolbarButton>
            <Typography variant="caption" sx={(t) => ({
              color: problem ? t.tokens.semantic.danger : t.tokens.text.tertiary,
            })}>
              {problem || 'Nothing goes to a carrier until you have looked the customer file over.'}
            </Typography>
          </Stack>
        </Stack>
      </Panel>

      <Panel
        title="What to drop in"
        subtitle="It reads whatever arrives; these three are what a household usually sends"
      >
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
          {SLOTS.map((slot) => (
            <Stack key={slot.key} spacing={0.5} sx={(t) => ({
              p: 1.5,
              borderRadius: `${t.ds.radius.md}px`,
              border: `1px solid ${t.tokens.border.subtle}`,
              bgcolor: t.tokens.surface.raised,
            })}>
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>{slot.label}</Typography>
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{slot.detail}</Typography>
            </Stack>
          ))}
        </Box>
      </Panel>

    </Stack>
  );
}
